import { FC, useEffect, useMemo, useState } from 'react';
import {
  Grid,
  List,
  ListItem,
  Collapse,
  styled,
  Typography,
  createStyles,
  makeStyles,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SearchIcon from '@material-ui/icons/Search';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import Button from 'src/components/Button';
import { Dashboard } from 'src/types';
import { Theme } from 'src/theme/types/createPalette';
import { useUpdateDashboard } from 'src/containers/dashboard/hooks';
import { ShareModal, ShareTarget } from 'src/modals/ShareModal';
import {
  LeaveShareModal,
  LeaveShareConfirmState,
} from 'src/modals/LeaveShareModal';
import { renameDashboardFolder } from 'src/apis/dashboard';
import useAuth from 'src/hooks/useAuth';
import useNotification from 'src/hooks/useNotification';
import useReceivedShares from 'src/hooks/useReceivedShares';
import useOwnedShares from 'src/hooks/useOwnedShares';
import {
  canShareResources,
  getReceivedItemSharesForIds,
  isDashboardInReceivedFolderShare,
  leaveFolderShareWithItemShares,
  leaveReceivedShareById,
  matchReceivedFolderShare,
  matchReceivedShare,
} from 'src/utils/share';

import { DashboardItem } from './DashboardItem';
import { DashboardFolder } from './DashboardFolder';

export const useStyles: any = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      height: 'calc(100vh - 50px)',
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      maxHeight: '100%',
      overflow: 'auto',
    },
    addBtn: {
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #000000',
      fontWeight: 'normal',
      fontSize: 16,
    },
    collapseContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: '20px 12px',
      paddingRight: 0,
    },
    collapseTitle: {
      fontSize: 15,
      fontWeight: 500,
      flex: 1,
    },
    collapseCounter: {
      border: '1px solid #000000',
      borderRadius: 2,
      fontSize: 12,
      lineHeight: '12px',
      padding: 4,
      marginRight: 8,
    },
    searchBar: {
      width: '100%',
      border: '1px solid #C3C3C7',
      padding: '2px 8px',
      borderRadius: 36,

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
  })
);

export const DashboardTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: 20,
  lineHeight: '24px',
  fontWeight: 500,
}));

export const ListContainer = styled(List)(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
}));

export const ListItemContainer = styled(ListItem)(() => ({
  display: 'flex',
  flex: 1,
  gap: 20,

  '& .MuiListItemText-primary': {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 16,
  },
  '& .MuiListItemText-secondary': {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 14,
  },
}));

interface DashboardListProps {
  selectedDashboardId: string | null;
  dashboards: Dashboard[];
  title?: string;
  onChangeFavorite: (dashboardId: string, favorite: boolean) => void;
  onSelectDashboard: (dashboard: Dashboard) => void;
  isDashboardSelection?: boolean;
}

const DashboardList: FC<DashboardListProps> = ({
  title = 'Dashboards',
  dashboards,
  selectedDashboardId,
  onSelectDashboard,
  isDashboardSelection = false,
}) => {
  const classes = useStyles();
  const { user, fetchOrganization } = useAuth();
  const { handleSuccess, handleError } = useNotification();
  const {
    shares: receivedShares,
    isReady: receivedSharesReady,
    refreshReceivedShares,
    sharedDashboardIds,
    sharedDashboardFolderKeys,
  } = useReceivedShares();
  const {
    refreshOwnedShares,
    sharedOutDashboardIds,
    sharedOutDashboardFolderKeys,
  } = useOwnedShares();
  const canShare = canShareResources(user?.role);
  const [openUncategorized, setOpenUncategorized] = useState<boolean>(true);
  const [openFolders, setOpenFolders] = useState<boolean>(true);
  const [openShared, setOpenShared] = useState<boolean>(true);
  const [openRecentlyViewed, setOpenRecentlyViewed] = useState<boolean>(true);
  const { onUpdateMultiDashboards } = useUpdateDashboard();
  const [uncategoriedDashbords, setUncategoriedDashboards] = useState<
    Dashboard[]
  >([]);
  const [folders, setFolders] = useState<Record<string, Dashboard[]>>({});
  const [activeDashboard, setActiveDashboard] = useState<Dashboard | null>(
    null
  );
  const [searchText, setSearchText] = useState<string>('');
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [leaveConfirm, setLeaveConfirm] =
    useState<LeaveShareConfirmState | null>(null);

  // const handleClickFavorite = (event: MouseEvent, dashboard: Dashboard) => {
  //   event.stopPropagation();
  //   onChangeFavorite(dashboard.id, !dashboard.favorite);
  // };

  // Sidebar remounts when opened — refresh dashboard list (owned + shared).
  // /share/received is refreshed by useReceivedShares on mount.
  useEffect(() => {
    void fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    const newUncategoriedDashbords: Dashboard[] = [];
    const newFolders: Record<string, Dashboard[]> = {};
    dashboards.forEach((item: Dashboard) => {
      if (item.isShared) {
        return;
      }
      if (item.folder) {
        if (newFolders[item.folder]) {
          newFolders[item.folder] = [...newFolders[item.folder], item];
        } else {
          newFolders[item.folder] = [item];
        }
      } else {
        newUncategoriedDashbords.push(item);
      }
    });
    setFolders((prev) => {
      Object.keys(prev).forEach((key) => {
        if (
          !newFolders[key] &&
          !sharedDashboardFolderKeys.has(key) &&
          (prev[key] || []).length === 0
        ) {
          newFolders[key] = [];
        }
      });
      return newFolders;
    });
    setUncategoriedDashboards(newUncategoriedDashbords);
  }, [dashboards, sharedDashboardFolderKeys]);

  const sharedFolders = useMemo(() => {
    const next: Record<string, Dashboard[]> = {};
    dashboards.forEach((item) => {
      if (
        !item.isShared ||
        !item.folder ||
        !sharedDashboardFolderKeys.has(item.folder)
      ) {
        return;
      }
      next[item.folder] = [...(next[item.folder] || []), item];
    });
    return next;
  }, [dashboards, sharedDashboardFolderKeys]);

  const sharedLooseDashboards = useMemo(
    () =>
      dashboards.filter(
        (item) =>
          item.isShared &&
          !isDashboardInReceivedFolderShare(
            item.folder,
            sharedDashboardFolderKeys
          )
      ),
    [dashboards, sharedDashboardFolderKeys]
  );

  // Wait for /share/received so folder shares aren't briefly shown as loose items.
  const hasSharedContent =
    receivedSharesReady &&
    (Object.keys(sharedFolders).length > 0 || sharedLooseDashboards.length > 0);

  useEffect(() => {
    if (selectedDashboardId) {
      const dashboard = dashboards.find(
        (item) => item.id === selectedDashboardId
      );
      setActiveDashboard(dashboard || dashboards[0]);
    } else {
      setActiveDashboard(dashboards[0]);
    }
  }, [dashboards, selectedDashboardId]);

  const handleNewFolder = () => {
    let folderName = 'New Folder';
    for (let index = 0; index < 10000; index++) {
      if (folders[folderName]) {
        folderName = `New Folder ${index + 1}`;
      } else {
        break;
      }
    }
    const newFolders: Record<string, Dashboard[]> = {
      ...folders,
    };
    newFolders[folderName] = [];
    setFolders(newFolders);
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || !source) {
      return;
    }

    if (destination.droppableId === source.droppableId) {
      return;
    }

    let dragItem: Dashboard | null = null;
    const tempUncategoriedDashbords = [...uncategoriedDashbords];
    const tempFolders = { ...folders };

    if (source.droppableId === 'Uncategorized Dashboards') {
      dragItem = tempUncategoriedDashbords[source.index];
      tempUncategoriedDashbords.splice(source.index, 1);
    } else {
      dragItem = folders[source.droppableId][source.index];
      const temp = [...tempFolders[source.droppableId]];
      temp.splice(source.index, 1);
      tempFolders[source.droppableId] = temp;
    }

    if (!dragItem || dragItem.isShared) {
      return;
    }

    if (destination.droppableId === 'Uncategorized Dashboards') {
      tempUncategoriedDashbords.push(dragItem);
    } else {
      tempFolders[destination.droppableId] = [
        ...tempFolders[destination.droppableId],
        dragItem,
      ];
    }

    setUncategoriedDashboards(tempUncategoriedDashbords);
    setFolders(tempFolders);
    onUpdateMultiDashboards([
      {
        dashboardId: dragItem.id,
        data: {
          name: dragItem.name,
          type: dragItem.type,
          keywordsList: dragItem.keywordsList,
          folder:
            destination.droppableId === 'Uncategorized Dashboards'
              ? null
              : destination.droppableId,
          exceptionFilterId: dragItem.exceptionFilterId,
        },
      },
    ]);
  };

  const handleDeleteFolder = (folderName: string) => {
    const folderItems = folders[folderName] || [];
    const ownedItems = folderItems.filter((item) => !item.isShared);
    const sharedItems = folderItems.filter((item) => item.isShared);
    const folderDashboards = ownedItems.map((item) => ({
      dashboardId: item.id,
      data: {
        name: item.name,
        type: item.type,
        keywordsList: item.keywordsList,
        folder: null,
        exceptionFilterId: item.exceptionFilterId,
      },
    }));
    const tempFolders = { ...folders };
    setUncategoriedDashboards([...uncategoriedDashbords, ...ownedItems]);
    if (sharedItems.length) {
      tempFolders[folderName] = sharedItems;
    } else {
      delete tempFolders[folderName];
    }
    setFolders(tempFolders);
    if (folderDashboards.length) {
      onUpdateMultiDashboards(folderDashboards);
    }
  };

  const handleRenameFolder = async (folderName: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === folderName) {
      return;
    }

    const previousFolders = folders;
    const folderItems = folders[folderName] || [];
    const tempFolders = { ...folders };
    tempFolders[trimmed] = (tempFolders[folderName] || []).map((item) => ({
      ...item,
      folder: trimmed,
    }));
    delete tempFolders[folderName];
    setFolders(tempFolders);

    if (!folderItems.length) {
      return;
    }

    try {
      await renameDashboardFolder(folderName, trimmed);
      handleSuccess('Folder renamed');
      await fetchOrganization();
    } catch (error) {
      setFolders(previousFolders);
      handleError(error, 'Could not rename folder');
      await fetchOrganization();
    }
  };

  const handleChangeSearchText = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleShareFolder = (folderName: string) => {
    setShareTarget({
      type: 'dashboardFolder',
      name: folderName,
      resourceId: folderName,
    });
  };

  const handleShareDashboard = (dashboard: Dashboard) => {
    setShareTarget({
      type: 'dashboard',
      name: dashboard.name,
      resourceId: dashboard.id,
    });
  };

  const handleLeaveDashboard = (dashboard: Dashboard) => {
    const share = matchReceivedShare(receivedShares, 'DASHBOARD', dashboard.id);
    if (!share) {
      handleError(null, 'No matching share found for this dashboard.');
      return;
    }

    setLeaveConfirm({
      title: 'Leave shared dashboard',
      message: `Leave shared dashboard "${dashboard.name}"? Your organization will lose access.`,
      onConfirm: async () => {
        try {
          await leaveReceivedShareById(share.id);
          handleSuccess(`Left shared dashboard "${dashboard.name}".`);
          if (selectedDashboardId === dashboard.id) {
            const next =
              dashboards.find(
                (item) => item.id !== dashboard.id && !item.isShared
              ) || dashboards.find((item) => item.id !== dashboard.id);
            if (next) {
              onSelectDashboard(next);
            }
          }
          await Promise.all([fetchOrganization(), refreshReceivedShares()]);
        } catch (error) {
          handleError(error, 'Unable to leave this shared dashboard.');
          throw error;
        }
      },
    });
  };

  const handleLeaveFolder = (folderName: string) => {
    const share = matchReceivedFolderShare(
      receivedShares,
      'DASHBOARD_FOLDER',
      folderName
    );
    if (!share) {
      handleError(null, 'No matching share found for this folder.');
      return;
    }

    const folderDashboards = dashboards.filter(
      (item) => item.folder === folderName
    );
    const itemShares = getReceivedItemSharesForIds(
      receivedShares,
      'DASHBOARD',
      folderDashboards.map((item) => item.id)
    );

    setLeaveConfirm({
      title: 'Leave shared folder',
      message: itemShares.length
        ? `Leave shared folder "${folderName}"? This also removes access from dashboards that were shared individually in this folder.`
        : `Leave shared folder "${folderName}"? Your organization will lose access to dashboards in this folder.`,
      onConfirm: async () => {
        try {
          await leaveFolderShareWithItemShares(share, itemShares);
          handleSuccess(`Left shared folder "${folderName}".`);
          const stillVisible = dashboards.filter(
            (item) => !(item.folder === folderName && item.isShared)
          );
          if (
            selectedDashboardId &&
            !stillVisible.some((item) => item.id === selectedDashboardId) &&
            stillVisible[0]
          ) {
            onSelectDashboard(stillVisible[0]);
          }
          await Promise.all([fetchOrganization(), refreshReceivedShares()]);
        } catch (error) {
          handleError(error, 'Unable to leave this shared folder.');
          throw error;
        }
      },
    });
  };

  return (
    <div className={classes.container}>
      <DashboardTitle variant='h5'>{title}</DashboardTitle>
      <TextField
        InputLabelProps={{
          shrink: true,
          style: { fontWeight: 'normal' },
        }}
        InputProps={{
          placeholder: 'Search all dashboards...',
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon htmlColor='#858591' style={{ fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        value={searchText}
        onChange={handleChangeSearchText}
        className={classes.searchBar}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.mainContent}>
          {!isDashboardSelection && (
            <Grid container>
              <Grid
                container
                item
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                onClick={() => {
                  setOpenRecentlyViewed(!openRecentlyViewed);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Typography className={classes.collapseTitle}>
                  Recently Viewed
                </Typography>
                {openRecentlyViewed ? (
                  <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                )}
              </Grid>
              <Collapse
                in={openRecentlyViewed}
                timeout='auto'
                unmountOnExit
                style={{ width: '100%' }}
              >
                <Grid container className={classes.collapseContainer}>
                  {activeDashboard && (
                    <DashboardItem
                      key={activeDashboard.id}
                      index={0}
                      dashboard={activeDashboard}
                      noDraggable
                      showActions={!isDashboardSelection && canShare}
                      onSelectDashboard={onSelectDashboard}
                      onShareDashboard={
                        canShare ? handleShareDashboard : undefined
                      }
                      onLeaveDashboard={
                        canShare &&
                        activeDashboard.isShared &&
                        sharedDashboardIds.has(activeDashboard.id) &&
                        !isDashboardInReceivedFolderShare(
                          activeDashboard.folder,
                          sharedDashboardFolderKeys
                        )
                          ? handleLeaveDashboard
                          : undefined
                      }
                      hideSharedLabel={Boolean(activeDashboard.isShared)}
                      isSharedOut={
                        !activeDashboard.isShared &&
                        sharedOutDashboardIds.has(activeDashboard.id)
                      }
                      sharedOutOrganizationCount={sharedOutDashboardIds.get(
                        activeDashboard.id
                      )}
                    />
                  )}
                </Grid>
              </Collapse>
            </Grid>
          )}
          <Grid container>
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              onClick={() => {
                setOpenFolders(!openFolders);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography className={classes.collapseTitle}>Folders</Typography>
              <Typography className={classes.collapseCounter}>
                {Object.keys(folders).length}
              </Typography>
              {openFolders ? (
                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
              )}
            </Grid>
            <Collapse
              in={openFolders}
              timeout='auto'
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Grid container className={classes.collapseContainer}>
                {Object.keys(folders).map((key) => (
                  <DashboardFolder
                    key={key}
                    name={key}
                    dashboards={folders[key].filter((item) =>
                      item.name
                        .toLowerCase()
                        .includes(searchText.trim().toLowerCase())
                    )}
                    onDeleteFolder={handleDeleteFolder}
                    onRenameFolder={handleRenameFolder}
                    onShareFolder={canShare ? handleShareFolder : undefined}
                    onShareDashboard={
                      canShare ? handleShareDashboard : undefined
                    }
                    onSelectDashboard={onSelectDashboard}
                    isSharedOut={sharedOutDashboardFolderKeys.has(key)}
                    sharedOutOrganizationCount={sharedOutDashboardFolderKeys.get(
                      key
                    )}
                    sharedOutDashboardIds={sharedOutDashboardIds}
                    isDashboardSelection={isDashboardSelection}
                  />
                ))}
              </Grid>
            </Collapse>
          </Grid>
          <Droppable droppableId='Uncategorized Dashboards'>
            {(provided) => (
              <Grid
                container
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <Grid
                  container
                  item
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  onClick={() => {
                    setOpenUncategorized(!openUncategorized);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography className={classes.collapseTitle}>
                    Uncategorized Dashboards
                  </Typography>
                  <Typography className={classes.collapseCounter}>
                    {
                      uncategoriedDashbords.filter((item) =>
                        item.name
                          .toLowerCase()
                          .includes(searchText.trim().toLowerCase())
                      ).length
                    }
                  </Typography>
                  {openUncategorized ? (
                    <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                  )}
                </Grid>
                <Collapse
                  in={openUncategorized}
                  timeout='auto'
                  unmountOnExit
                  style={{ width: '100%' }}
                >
                  <Grid container className={classes.collapseContainer}>
                    {uncategoriedDashbords
                      .filter((item) =>
                        item.name
                          .toLowerCase()
                          .includes(searchText.trim().toLowerCase())
                      )
                      .map((dashboard, index) => (
                        <DashboardItem
                          key={dashboard.id}
                          index={index}
                          dashboard={dashboard}
                          showActions={!isDashboardSelection && canShare}
                          onSelectDashboard={onSelectDashboard}
                          onShareDashboard={
                            canShare ? handleShareDashboard : undefined
                          }
                          isSharedOut={sharedOutDashboardIds.has(dashboard.id)}
                          sharedOutOrganizationCount={sharedOutDashboardIds.get(
                            dashboard.id
                          )}
                        />
                      ))}
                  </Grid>
                  {provided.placeholder}
                </Collapse>
              </Grid>
            )}
          </Droppable>
          {hasSharedContent && (
            <Grid container>
              <Grid
                container
                item
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                onClick={() => {
                  setOpenShared(!openShared);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Typography className={classes.collapseTitle}>
                  Shared
                </Typography>
                <Typography className={classes.collapseCounter}>
                  {Object.keys(sharedFolders).length +
                    sharedLooseDashboards.filter((item) =>
                      item.name
                        .toLowerCase()
                        .includes(searchText.trim().toLowerCase())
                    ).length}
                </Typography>
                {openShared ? (
                  <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                )}
              </Grid>
              <Collapse
                in={openShared}
                timeout='auto'
                unmountOnExit
                style={{ width: '100%' }}
              >
                <Grid container className={classes.collapseContainer}>
                  {Object.keys(sharedFolders).map((key) => (
                    <DashboardFolder
                      key={`shared-${key}`}
                      name={key}
                      dashboards={sharedFolders[key].filter((item) =>
                        item.name
                          .toLowerCase()
                          .includes(searchText.trim().toLowerCase())
                      )}
                      sharedSection
                      onLeaveFolder={canShare ? handleLeaveFolder : undefined}
                      onSelectDashboard={onSelectDashboard}
                      isDashboardSelection={isDashboardSelection}
                    />
                  ))}
                  {sharedLooseDashboards
                    .filter((item) =>
                      item.name
                        .toLowerCase()
                        .includes(searchText.trim().toLowerCase())
                    )
                    .map((dashboard, index) => (
                      <DashboardItem
                        key={dashboard.id}
                        index={index}
                        dashboard={dashboard}
                        noDraggable
                        showActions={!isDashboardSelection && canShare}
                        onSelectDashboard={onSelectDashboard}
                        onLeaveDashboard={
                          canShare && sharedDashboardIds.has(dashboard.id)
                            ? handleLeaveDashboard
                            : undefined
                        }
                        hideSharedLabel
                      />
                    ))}
                </Grid>
              </Collapse>
            </Grid>
          )}
        </div>
      </DragDropContext>
      {!isDashboardSelection && (
        <Button
          title='+ Add new folder'
          color='primary'
          fontSize='sm'
          height={36}
          width='100%'
          className={classes.addBtn}
          onClick={handleNewFolder}
        />
      )}
      <ShareModal
        open={!!shareTarget}
        target={shareTarget}
        closeModal={() => setShareTarget(null)}
        onSharesChanged={refreshOwnedShares}
      />
      <LeaveShareModal
        open={!!leaveConfirm}
        confirm={leaveConfirm}
        closeModal={() => setLeaveConfirm(null)}
      />
    </div>
  );
};

export default DashboardList;
